import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import api, { formatCurrency, paymentMethodLabels } from '../../lib/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = ['hsl(221, 83%, 53%)', 'hsl(48, 96%, 53%)', 'hsl(265, 83%, 60%)'];

const AdminFinancial = () => {
  const [report, setReport] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportRes, salesRes] = await Promise.all([
        api.get('/admin/reports/financial'),
        api.get(`/admin/reports/sales?period=${period}`),
      ]);
      setReport(reportRes.data);
      setSalesData(salesRes.data.data || []);
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [period]);

  const paymentData = report?.by_payment_method?.map(item => ({
    name: paymentMethodLabels[item._id] || item._id,
    value: item.total,
    count: item.count,
  })) || [];

  return (
    <AdminLayout title="Relatórios Financeiros">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Vendido</p>
                <p className="text-2xl font-bold font-heading text-success" data-testid="total-sales">
                  {formatCurrency(report?.total_sales || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {report?.sales_count || 0} vendas
                </p>
              </div>
              <div className="p-3 rounded-xl bg-success">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Comprado</p>
                <p className="text-2xl font-bold font-heading text-destructive" data-testid="total-purchases">
                  {formatCurrency(report?.total_purchases || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Custos</p>
              </div>
              <div className="p-3 rounded-xl bg-destructive">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Lucro Bruto</p>
                <p className={`text-2xl font-bold font-heading ${(report?.gross_profit || 0) >= 0 ? 'text-success' : 'text-destructive'}`} data-testid="gross-profit">
                  {formatCurrency(report?.gross_profit || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Período atual</p>
              </div>
              <div className="p-3 rounded-xl bg-primary">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Margem de Lucro</p>
                <p className="text-2xl font-bold font-heading" data-testid="profit-margin">
                  {report?.profit_margin || 0}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">Média</p>
              </div>
              <div className="p-3 rounded-xl bg-secondary">
                <TrendingUp className="w-5 h-5 text-secondary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sales Over Time */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">Evolução de Vendas</CardTitle>
            <Tabs value={period} onValueChange={setPeriod}>
              <TabsList className="h-8">
                <TabsTrigger value="daily" className="text-xs h-7">Diário</TabsTrigger>
                <TabsTrigger value="monthly" className="text-xs h-7">Mensal</TabsTrigger>
                <TabsTrigger value="yearly" className="text-xs h-7">Anual</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="sales-evolution-chart">
              {loading ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Carregando...
                </div>
              ) : salesData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Nenhum dado disponível
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="_id" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => {
                        if (period === 'daily') return value?.slice(-5) || '';
                        if (period === 'monthly') return value?.slice(-7) || '';
                        return value;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `R$${value}`}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Vendas']}
                      labelFormatter={(label) => `Período: ${label}`}
                    />
                    <Bar dataKey="total" fill="hsl(221, 83%, 53%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading">Vendas por Forma de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="payment-methods-chart">
              {loading ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Carregando...
                </div>
              ) : paymentData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Nenhum dado disponível
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {paymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Details Table */}
      <Card className="rounded-2xl mt-6">
        <CardHeader>
          <CardTitle className="font-heading">Detalhamento por Forma de Pagamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="admin-table" data-testid="payment-details-table">
              <thead>
                <tr>
                  <th>Forma de Pagamento</th>
                  <th>Quantidade</th>
                  <th>Total</th>
                  <th>% do Total</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-muted-foreground">
                      Nenhum dado disponível
                    </td>
                  </tr>
                ) : (
                  paymentData.map((item, index) => {
                    const percentage = report?.total_sales > 0 
                      ? ((item.value / report.total_sales) * 100).toFixed(1) 
                      : 0;
                    return (
                      <tr key={index}>
                        <td className="font-medium">{item.name}</td>
                        <td>{item.count} vendas</td>
                        <td className="font-medium">{formatCurrency(item.value)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full"
                                style={{ 
                                  width: `${percentage}%`,
                                  backgroundColor: COLORS[index % COLORS.length]
                                }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground w-12">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminFinancial;
